import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Package, Truck, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import TubelightNavbar from '@/components/TubelightNavbar';
import FooterSection from '@/components/FooterSection';
import Confetti from 'react-confetti';

const ThankYouPage = () => {
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [showConfetti, setShowConfetti] = useState(true);
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const lastOrder = localStorage.getItem('lastOrder');
    if (lastOrder) {
      setOrder(JSON.parse(lastOrder));
    } else {
      navigate('/products');
    }

    if (typeof window !== 'undefined') {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    }

    setTimeout(() => setShowConfetti(false), 5000);
  }, [navigate]);

  if (!order) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50">
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={500}
        />
      )}

      <TubelightNavbar />

      <div className="pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="inline-block mb-6"
            >
              <CheckCircle size={80} className="text-green-500" />
            </motion.div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Thank You for Your Order!
            </h1>
            <p className="text-lg sm:text-xl text-gray-600">
              Your order has been confirmed and will be delivered in 2-3 working days.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="p-6 sm:p-8 mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 pb-6 border-b border-gray-200">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Order ID</p>
                  <p className="text-xl sm:text-2xl font-bold" data-testid="order-id">{order.orderId}</p>
                </div>
                <div className="mt-4 sm:mt-0">
                  <span className="inline-block px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                    Order Confirmed
                  </span>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-lg mb-4 flex items-center">
                    <Package className="mr-2" size={20} />
                    Order Items
                  </h3>
                  <div className="space-y-4">
                    {(order.items || []).map((item, index) => (
                      <div key={index} className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                        <img
                          src={item.image || item.variant?.image || 'https://via.placeholder.com/200'}
                          alt={item.productTitle || item.title || item.product?.title || 'Product'}
                          className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg"
                        />
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm sm:text-base line-clamp-1">
                            {item.productTitle || item.title || item.product?.title || 'Product'}
                          </h4>
                          <p className="text-xs sm:text-sm text-gray-500 mt-1">
                            {item.color || item.variant?.color || 'Default'}
                          </p>
                          <p className="text-sm sm:text-base font-medium mt-2">
                            ₹{item.price || item.product?.price || 0} x {item.quantity || 1}
                          </p>
                        </div>
                        <div className="font-bold text-sm sm:text-base">
                          ₹{(item.price || item.product?.price || 0) * (item.quantity || 1)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-200">
                  <div className="flex justify-between text-sm sm:text-base mb-2">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">₹{order.total}</span>
                  </div>
                  <div className="flex justify-between text-sm sm:text-base mb-2">
                    <span className="text-gray-600">Shipping</span>
                    <span className="font-medium text-green-600">FREE</span>
                  </div>
                  <div className="flex justify-between text-lg sm:text-xl font-bold pt-2 border-t border-gray-200">
                    <span>Total</span>
                    <span>₹{order.total}</span>
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-200">
                  <h3 className="font-semibold text-lg mb-4 flex items-center">
                    <MapPin className="mr-2" size={20} />
                    Delivery Address
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg text-sm sm:text-base">
                    <p className="font-medium">{order.customer.fullName}</p>
                    <p className="text-gray-600 mt-1">{order.customer.phone}</p>
                    <p className="text-gray-600 mt-1">{order.customer.email}</p>
                    <p className="text-gray-600 mt-2">
                      {order.customer.houseNo}, {order.customer.street}<br />
                      {order.customer.city}, {order.customer.state} - {order.customer.pincode}
                    </p>
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg flex items-start">
                  <Truck className="text-blue-600 mt-1 mr-3 flex-shrink-0" size={24} />
                  <div>
                    <p className="font-semibold text-blue-900">Expected Delivery</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Your order will be delivered within 2-3 working days. You will receive tracking details via email.
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-6">
              <p className="text-sm text-yellow-800 font-medium">
                📋 Save your Order ID: <span className="font-bold">{order.orderId}</span>
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                You'll need this Order ID to track your order status
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="flex-1 py-6 text-base rounded-full bg-black hover:bg-gray-900"
                onClick={() => navigate('/my-orders')}
                data-testid="track-order-button"
              >
                Track Order
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="flex-1 py-6 text-base rounded-full"
                onClick={() => navigate('/')}
                data-testid="back-home-button"
              >
                Back to Home
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      <FooterSection />
    </div>
  );
};

export default ThankYouPage;